// ✅ Static metadata export (used in project listing page)
export const metadata = {
  title: 'Luxury Kitchen Renovation in Encino, Los Angeles CA 91316',
  description:
    'A high-end kitchen remodeling project in Encino featuring custom cabinetry, premium countertops, modern appliances, and refined finishes by Modern Build.',
  link: '/services/kitchen-remodeling',
  height: 'h-[467px]',
  image:
    '/projects/luxury-kitchen-renovation-in-encino-los-angeles-91316/luxury-kitchen-renovation-in-encino-los-angeles-91316.webp',
};

import fs from 'fs';
import path from 'path';
import Head from 'next/head';
import Header from '../../../components/Header';
import Hero from '../../../components/Hero';
import ContentBlock from '../../../components/ContentBlock';
import { VideoSection } from '../../../components/Videos';
import Gallery from '../../../components/Gallery';
import SocialProof from '../../../components/SocialProof';
import ContactUs from '../../../components/ContactUs';
import Footer from '../../../components/Footer';
import { generateWebPageSchema } from '../../../utils/generateWebPageSchema';

export async function getStaticProps() {
  const slug = 'luxury-kitchen-renovation-in-encino-los-angeles-91316';

  const galleryPath = path.join(
    process.cwd(),
    'public',
    'projects',
    slug,
    'gallery'
  );

  let galleryImages = [];

  // ✅ Load gallery images
  if (fs.existsSync(galleryPath)) {
    const files = fs.readdirSync(galleryPath).filter(file =>
      /\.(jpg|jpeg|png|webp)$/i.test(file)
    );

    galleryImages = files.map(file => ({
      src: `/projects/${slug}/gallery/${file}`,
      alt: file
        .replace(/\.(jpg|jpeg|png|webp)$/i, '')
        .replace(/[-_]/g, ' '),
      title: file
        .replace(/\.(jpg|jpeg|png|webp)$/i, '')
        .replace(/[-_]/g, ' '),
    }));
  }

  return {
    props: {
      galleryImages,
    },
  };
}

export default function LuxuryKitchenEncino({ galleryImages }) {
  const meta = metadata;

  return (
    <>
      <Head>
        <title>{meta.title}</title>
        <meta name="title" content={meta.title} />
        <meta name="description" content={meta.description} />
        <meta name="author" content="Modern Build Home Remodeling" />
        <meta
          name="keywords"
          content="luxury kitchen remodel Encino, modern kitchen renovation 91316, custom cabinetry Los Angeles, premium countertops, Modern Build"
        />
        <link
          rel="canonical"
          href="https://modernbuildremodel.com/projects/luxury-kitchen-renovation-in-encino-los-angeles-91316"
        />

        {/* Open Graph */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta
          property="og:image"
          content={`https://modernbuildremodel.com${meta.image}`}
        />
        <meta
          property="og:url"
          content="https://modernbuildremodel.com/projects/luxury-kitchen-renovation-in-encino-los-angeles-91316"
        />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={meta.title}
        />
        <meta
          name="twitter:description"
          content={meta.description}
        />
        <meta
          name="twitter:image"
          content={`https://modernbuildremodel.com${meta.image}`}
        />

        {/* Breadcrumb Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://modernbuildremodel.com' },
                { '@type': 'ListItem', position: 2, name: 'Projects', item: 'https://modernbuildremodel.com/projects' },
                { '@type': 'ListItem', position: 3, name: 'Luxury Kitchen Renovation in Encino CA 91316', item: 'https://modernbuildremodel.com/projects/luxury-kitchen-renovation-in-encino-los-angeles-91316' },
              ],
            }),
          }}
        />

        {/* WebPage Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Luxury Kitchen Renovation in Encino, Los Angeles CA 91316 | Modern Build',
                description: 'Explore a high-end kitchen remodel in Encino with custom cabinetry, premium countertops, modern appliances, and refined finishes.',
                slug: '/projects/luxury-kitchen-renovation-in-encino-los-angeles-91316',
              })
            ),
          }}
        />
      </Head>

      <Header />

      <Hero category="luxuryKitchenEncino91316" page="page" hasContentH1={false}/>

      <ContentBlock
        subtitle="PROJECT OVERVIEW"
        title="Luxury Kitchen Renovation in Encino"
        description="This high-end kitchen remodel in Encino transformed a dated space into a functional, modern, and luxurious culinary environment."
        extraText="Every detail was crafted to maximize efficiency, style, and long-lasting appeal."
        buttonText="Plan My Kitchen Remodel"
        buttonLink="/services/kitchen-remodeling"
      />

      <Gallery images={galleryImages} />

      <section className="bg-white py-[120px]">
        <div className="container mx-auto px-6">
          <VideoSection
            videoUrl="https://www.youtube.com/embed/aptLiTK9xBo"
            serviceType="Kitchen Renovation"
            location="Encino, Los Angeles"
            description="Watch how this luxury kitchen in Encino was completely transformed with custom cabinets, modern appliances, and elegant finishes."
            serviceLink="/services/kitchen-remodeling"
            coverImage={meta.image}
            isVideoLeft={true}
          />
        </div>
      </section>

<section className="bg-white py-[120px]">
  <div className="container mx-auto px-6 max-w-4xl">

    <h2 className="text-2xl font-light text-primary leading-tight font-gilda mb-4">
      Luxury Kitchen Renovation in Encino, Los Angeles CA 91316
    </h2>

    <p className="text-gray font-light text-lg leading-relaxed">
      This luxury kitchen renovation in Encino, Los Angeles CA 91316 transformed a dated, inefficient space into a modern culinary masterpiece. Every choice, from cabinetry to countertops, lighting, and appliances, was carefully curated to provide a balance of style, comfort, and long-lasting quality.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      The homeowners wanted a kitchen that met daily cooking needs and reflected their sophisticated lifestyle. Our team assessed the layout, enhanced storage, and integrated modern appliances seamlessly. For related home improvements, see our <a
        href="https://modernbuildremodel.com/complete-home-remodeling/california/encino/91316"
        className="text-primary underline"
      >
        Complete Home Remodeling
      </a> services.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Design Concept and Strategy
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      The design strategy blended modern luxury with practical functionality. Custom cabinetry in a neutral palette complemented the home’s architecture, with specialized storage, pull-out shelves, and hidden compartments keeping countertops clutter-free.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      High-end quartz countertops and backsplash materials provide durability and elegance. The central island offers counter space, storage, and a casual dining area while acting as a hub for entertaining. Lighting design balances task needs and ambiance.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Premium Appliances and Functional Upgrades
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      Professional-grade appliances were integrated seamlessly, including range, built-in refrigerator, dual ovens, dishwasher, wine cooler, and built-in coffee station. Smart home controls enhance convenience. Storage and ergonomics were carefully planned for efficiency.
    </p>

    <p className="text-gray font-light text-lg leading-relaxed">
      For more high-end kitchen transformations, explore our <a
        href="https://modernbuildremodel.com/kitchen-remodeling/california/encino/91316"
        className="text-primary underline"
      >
        Kitchen Remodeling
      </a> services.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Craftsmanship and Materials
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      Cabinetry was handcrafted with precise detailing. Countertops and backsplashes feature high-quality stonework, and flooring was selected for comfort and durability. Finishing touches include custom hardware, elegant faucets, and integrated lighting accents.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Homeowner Experience and Lifestyle Impact
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      Cooking, entertaining, and casual family meals are now seamless experiences within a beautifully curated environment. This renovation improved both aesthetics and daily life, while adding long-term value.
    </p>

    <h3 className="text-xl font-light text-black leading-tight font-gilda mt-10 mb-4">
      Serving Encino and Los Angeles Communities
    </h3>

    <p className="text-gray font-light text-lg leading-relaxed">
      Modern Build proudly serves Encino and Los Angeles communities with high-end kitchen renovations. Whether your goal is a complete kitchen overhaul or targeted luxury upgrades, our team delivers exceptional results tailored to your lifestyle.
    </p>

  </div>
</section>

      <SocialProof />
      <ContactUs />
      <Footer />
    </>
  );
}
